import {
  useEffect,
  useMemo,
  ComponentProps,
} from 'react';
import { inject, observer } from 'mobx-react';

import { SelectInput, MultiSelectInput } from '../forms';
import { servicesActions, ServicesStore } from '../../../permissions/store/servicesStore';

type ServiceSelectorProps = Omit<ComponentProps<typeof SelectInput | typeof MultiSelectInput>, 'value' | 'defaultValue'> & {
  name?: string,
  /** Показывать только сервисы партнёра авторизовавшегося пользователя (partnerId игнорируется). */
  onlyPartnerService?: boolean,
  /** Показывать только стационарные сервисы. */
  onlyStationary?: boolean,
  /** Показывать только НЕ стационарные сервисы. */
  onlyNoStationary?: boolean,
  /** Пользовательский фильтр списка сервисов. */
  filter?: (item: any) => boolean,
  /** Партнёр, чьи сервисы показываем (ветка без onlyPartnerService). */
  partnerId?: string,
  multiSelect?: boolean,
  /**
   * Исключить сервисы с этими id. Формально unknown[]: в legacy-потребителях
   * встречаются и строки (servicePromos), и даже объекты (см. баг-реестр в SELECTORS_TSX_PLAN.md).
   */
  excludeServices?: unknown[],
  /** Показать только сервисы с этими id. */
  includeServices?: Array<number>,
  /** С onlyPartnerService: грузить сервисы авторизованного пользователя, а не партнёра. */
  byUser?: boolean,
  /**
   * Преобразование значения поля Formik в выбранные опции для отображения.
   *
   * По умолчанию (для multiSelect) ServiceSelector использует собственный маппер,
   * поддерживающий оба формата начального значения: массив примитивов ([68]) и
   * массив объектов ([{ value: 68, label: '...' }]). Это нужно, потому что react-select
   * ждёт объекты, а в initialFilterValues часто приходят голые id.
   * Влияет только на отображение; формат хранения/возврата значения не меняется,
   * поэтому существующие обработчики `.map(v => v.value)` продолжают работать.
   */
  mapFieldToSelectedOptions?: (options: any[], values: unknown) => any[],
  /** из inject('servicesStore') */
  servicesStore?: ServicesStore,
};

// Стабильный дефолтный маппер значения поля → выбранные опции для отображения.
// Поддерживает начальное значение как массивом примитивов ([68]), так и массивом
// объектов ([{ value: 68, label: '...' }]), а также одиночное значение/null.
const defaultMapFieldToSelectedOptions = (options: any, vals: any): any => {
  const arr = Array.isArray(vals) ? vals : (vals ? [vals] : []);
  const selectedKeys = arr.map((k) => k?.value ?? k);
  return (options ?? []).filter((item) => selectedKeys.includes(item?.value));
};

const ServiceSelector = (props: ServiceSelectorProps) => {
  const {
    name = 'serviceId',
    servicesStore,
    partnerId, // по данному партнёру показываем сервисы
    onlyPartnerService, // показываем только сервисы по авторизовавшемуся пользователю партнёра
    // onlyPartnerService - если передан данные параметр, то partnerId игнорим
    filter,
    onlyStationary,
    onlyNoStationary,
    multiSelect = false,
    excludeServices,
    includeServices,
    byUser,
    // Выносим из rest, чтобы НЕ протекало в SelectInput (single-режим) —
    // react-select ругается на неизвестный проп. Передаём только в MultiSelectInput.
    mapFieldToSelectedOptions,
    ...rest
  } = props;

  const { services, partnerServices, loadingServices, loadingPartnerServices } = servicesStore;

  // Мемоизированная фильтрация — стабильная ссылка массива для data между рендерами
  const filteredData = useMemo(() => {
    let serviceData = [...((onlyPartnerService ? partnerServices : services) ?? [])];
    if (typeof filter === 'function') {
      serviceData = serviceData.filter(filter);
    }
    if (onlyStationary) { serviceData = serviceData.filter(v => v.stationary); }
    if (onlyNoStationary) { serviceData = serviceData.filter(v => !v.stationary); }
    if (excludeServices) { serviceData = serviceData.filter(v => !(excludeServices.find(k => v.id === k))); }
    if (includeServices) { serviceData = serviceData.filter(v => (includeServices.find(k => v.id === k))); }
    return serviceData;
  },
  [
    excludeServices, filter, includeServices, onlyNoStationary,
    onlyPartnerService, onlyStationary, partnerServices, services,
  ]);

  useEffect(() => {
    // гейт и single-flight внутри акшена стора — повторный mount с теми же params не шлёт запрос
    if (onlyPartnerService) {
      if (byUser === true) {
        servicesActions.getAuthenticatedUserServices();
      } else {
        servicesActions.getAuthenticatedPartnersServices();
      }
    } else {
      servicesActions.getServices(partnerId);
    }
  }, [partnerId, onlyPartnerService, byUser]);

  const loading = onlyPartnerService ? loadingPartnerServices : loadingServices;

  return (
    multiSelect
      ? (
        <MultiSelectInput
          { ...rest }
          name={ name }
          isMulti
          noOptionsMessage={ () => { return 'Нет вариантов'; } }
          data={ filteredData }
          dataTitle='name'
          dataValue='id'
          loading={ loading }
          sort
          mapFieldToSelectedOptions={ mapFieldToSelectedOptions ?? defaultMapFieldToSelectedOptions }
        />
      )
      : (
        <SelectInput
          { ...rest }
          name={ name }
          data={ filteredData }
          dataTitle='name'
          dataValue='id'
          loading={ loading }
          sort
        />
      )

  );
};

export default inject('servicesStore')(observer(ServiceSelector));
